import { useEffect, useState } from "react";
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from "capacitor-native-settings";

import FullScreenMobilePage from "../../FullScreenMobilePage/FullScreenMobilePage";
import Button from "../../Button/Button";

import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  biometricsOptInPage,
  logout,
  updateBiometricError,
} from "../../../store/slices/authSlice";
import { BiometricEnrollmentError } from "../../../constants";

import successIcon from "../../../assets/icons/success-light-icon.svg";
import infoIcon from "../../../assets/icons/info-light-icon.svg";
import warningIcon from "../../../assets/icons/warning-error-light-icon.svg";
import errorIcon from "../../../assets/icons/error-light-icon.svg";

import styles from "./BiometricErrorFeedback.module.scss";

const TEMPORARY_LOCKOUT_COUNT = 30;

const Icon = ({ modalType }: { modalType: string }) => {
  switch (modalType) {
    case "success":
      return <img src={successIcon} alt="Ícono de éxito" />;
    case "info":
      return <img src={infoIcon} alt="Ícono de información" />;
    case "warning-error":
      return <img src={warningIcon} alt="Ícono de advertencia" />;
    case "error":
      return <img src={errorIcon} alt="Ícono de error" />;
    default:
      return null;
  }
};

const BiometricErrorFeedback = () => {
  const [countdown, setCountdown] = useState(TEMPORARY_LOCKOUT_COUNT);
  const { biometrics } = useAppSelector((state) => state.authentication);

  const { content, footer } = styles;

  const dispatch = useAppDispatch();

  const biometricErrorType = biometrics?.error;

  useEffect(() => {
    // Reset countdown
    if (
      biometricErrorType === BiometricEnrollmentError.USER_TEMPORARY_LOCKOUT
    ) {
      setCountdown(TEMPORARY_LOCKOUT_COUNT);
    }
  }, [biometricErrorType]);

  // Countdown effect for USER_TEMPORARY_LOCKOUT
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (
      biometricErrorType === BiometricEnrollmentError.USER_TEMPORARY_LOCKOUT &&
      countdown > 0
    ) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [biometricErrorType, countdown]);

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `Reintentar en ${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleLogout = () => dispatch(logout());

  const getFeedbackCardData = () => {
    let title = "";
    let description = "";
    let buttonText = "";
    let secondaryButtonText = "";
    let onButtonClick = () => {};
    let onSeccondaryButtonClick = () => {};
    let modalType = "error" as string;
    let primaryButtonProps = {};

    switch (biometricErrorType) {
      case BiometricEnrollmentError.USER_CANCELLED:
        title = "Cancelaste la autenticación con factor biométrico";
        description =
          "Para ingresar con datos biométricos, primero configurá el desbloqueo de tu celular con huella o reconocimiento facial.";
        modalType = "success";
        buttonText = "Aceptar";
        onButtonClick = () => {
          handleLogout();
          dispatch(biometricsOptInPage(null));
          dispatch(updateBiometricError(null));
        };
        break;
      case BiometricEnrollmentError.BIOMETRIC_VERIFICATION_FAILED:
      // SYSTEM_CANCELLED covers iOS "App Cancel", "Not Interactive" and "System
      // Cancel" — cases where the OS aborted the prompt without the user doing
      // anything. UX-wise we treat it the same as a verification failure so the
      // user simply retries; the distinction is preserved in telemetry.
      case BiometricEnrollmentError.SYSTEM_CANCELLED:
        title = "No pudimos validar tu identidad";
        description = "Por favor, intentá nuevamente.";
        modalType = "error";
        buttonText = "Reintentar";
        onButtonClick = () => {
          // handleLogout();
          dispatch(biometricsOptInPage(null));
          dispatch(updateBiometricError(null));
        };
        break;
      case BiometricEnrollmentError.USER_LOCKOUT:
        title = "Límite de intentos alcanzado";
        description =
          "Realizaste demasiados intentos fallidos. Por favor, volvé a intentarlo en unos segundos.";
        buttonText = "Aceptar";
        modalType = "warning-error";
        onButtonClick = () => {
          handleLogout();
          dispatch(biometricsOptInPage(null));
          dispatch(updateBiometricError(null));
        };
        break;
      case BiometricEnrollmentError.USER_TEMPORARY_LOCKOUT:
        modalType = "warning-error";
        title = "Límite de intentos alcanzado";
        description =
          "Realizaste demasiados intentos fallidos. Por favor, volvé a intentarlo en unos segundos.";
        buttonText = countdown > 0 ? formatCountdown(countdown) : "Reintentar";

        primaryButtonProps = { disabled: countdown > 0 };

        secondaryButtonText = "Ingresá manualmente";
        onSeccondaryButtonClick = () => {
          handleLogout();
          dispatch(biometricsOptInPage(null));
          dispatch(updateBiometricError(null));
        };
        onButtonClick = () => {
          if (countdown === 0) {
            handleLogout();
            dispatch(biometricsOptInPage(null));
            dispatch(updateBiometricError(null));
          }
        };
        break;
      case BiometricEnrollmentError.BIOMETRY_NOT_AVAILABLE:
      // PASSCODE_NOT_SET is also a "biometry cannot be used until the user
      // configures their device" case, so we reuse the same flow that guides
      // them to Settings.
      case BiometricEnrollmentError.PASSCODE_NOT_SET:
        modalType = "info";
        title = "Activá el desbloqueo biométrico";
        description =
          "Para usar el ingreso con huella o reconocimiento facial, primero tenés que configurarlo en tu celular.";
        buttonText = "Ir a Configuraciones";
        onButtonClick = () => {
          NativeSettings.open({
            optionAndroid: AndroidSettings.Settings,
            optionIOS: IOSSettings.App,
          });
        };
        secondaryButtonText = "Ingresá manualmente";
        onSeccondaryButtonClick = () => {
          handleLogout();
          dispatch(biometricsOptInPage(null));
          dispatch(updateBiometricError(null));
        };
        break;
      case BiometricEnrollmentError.UNKNOWN_ERROR:
      default:
        title = "Algo salió mal";
        description = "Por favor, intentá nuevamente.";
        modalType = "error";
        buttonText = "Aceptar";
        onButtonClick = () => {
          handleLogout();
          dispatch(biometricsOptInPage(null));
          dispatch(updateBiometricError(null));
        };

        break;
    }
    return {
      title,
      description,
      modalType,
      buttonText,
      onButtonClick,
      onSeccondaryButtonClick,
      secondaryButtonText,
      primaryButtonProps,
    };
  };

  const {
    title,
    description,
    modalType,
    buttonText,
    onButtonClick,
    onSeccondaryButtonClick,
    secondaryButtonText,
    primaryButtonProps,
  } = getFeedbackCardData();

  return (
    <FullScreenMobilePage
      noLogo
      footerContent={
        <section className={footer}>
          {secondaryButtonText && (
            <Button
              variant="secondary"
              text={secondaryButtonText}
              onPress={onSeccondaryButtonClick}
            />
          )}
          {buttonText && (
            <Button
              variant="primary"
              text={buttonText}
              onPress={onButtonClick}
              {...primaryButtonProps}
            />
          )}
        </section>
      }
    >
      <div className={content}>
        <Icon modalType={modalType} />
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </FullScreenMobilePage>
  );
};

export default BiometricErrorFeedback;
